import { Http } from './http';
import {
  Dealer,
  DealerComment,
  DealerContract,
  DealerDocumentRequest,
  DealerPartial,
  Doctype,
  Document,
  DocumentStatus,
  User,
} from '@types';
import { UniqueCropProps } from '@pages/dealer/DealerListActions/DealerUniqueCropsField';

class DealerHttp extends Http {

  async getById(dealerId: number): Promise<Dealer> {
    const res = await this.get<Dealer>(`dealer/${dealerId}`);

    return res.data;
  }

  async getList(): Promise<Dealer[]> {
    const res = await this.get<Dealer[]>('dealer?_order=ASC&_sort=name&_start=0&_end=999999');

    return res.data;
  }

  async getAssociateList(q?: string): Promise<Dealer[]> {
    const res = await this.get<Dealer[]>(`/dealer-associate?_order=ASC&_sort=name&_start=0&_end=999999&q=${q || ''}`);

    return res.data;
  }

  async getRetailerList(q?: string): Promise<Dealer[]> {
    const res = await this.get<Dealer[]>(`/dealer-retailer?_order=ASC&_sort=name&_start=0&_end=999999&q=${q || ''}`);

    return res.data;
  }

  async getBuyerList(q?: string): Promise<Dealer[]> {
    const res = await this.get<Dealer[]>(`/dealer-buyer?_order=ASC&_sort=name&_start=0&_end=999999&q=${q || ''}`);

    return res.data;
  }

  async merge(id: number, mergeToId: number): Promise<Dealer> {
    const res = await this.delete<Dealer>(`dealer/${id}/${mergeToId}`);

    return res.data;
  }

  async saveDealerContracts(params: DealerContractParams): Promise<DealerContract[]> {
    const res = await this.post<DealerContract[]>('dealer/contract', params);

    return res.data;
  }

  async create(
    params: DealerPartial,
    documents: DealerDocumentRequest[],
    resource: string,
  ): Promise<Dealer> {
    const res = await this.post<Dealer>(resource, { ...params, documents });

    return res.data;
  }

  async update(
    params: Dealer,
    documents: DealerDocumentRequest[],
    resource: string,
  ): Promise<Dealer> {
    const { id } = params;
    const res = await this.put<Dealer>(`${resource}/${id}`, { ...params, documents });

    return res.data;
  }

  async remove(id: number, resource: string): Promise<Dealer> {
    const res = await this.delete<Dealer>(`${resource}/${id}`);

    return res.data;
  }

  async addBulkActivation(ids: number[], activationId: number) {
    const res = await this.post<void>('/dealer/add-bulk-activation', { ids, activationId });

    return res.data;
  }

  async removeActivation(id: number) {
    const res = await this.patch<string>(`/dealer/${id}/remove-activation`);

    return res.data;
  }

  async salesforceSync(id: number) {
    const res = await this.get<string>(`/dealer/${id}/salesforce-sync`);

    return res.data;
  }

  async deleteAccount(id: number) {
    const res = await this.delete<string>(`/dealer/${id}/delete-account`);

    return res.data;
  }

  async createComment(id: number, comment: string) {
    const res = await this.post<DealerComment>(`/dealer/${id}/comment`, { comment });

    return res.data;
  }

  async getComments(id: number) {
    const res = await this.get<DealerComment[]>(`/dealer/${id}/comment`);

    return res.data;
  }

  async updateBulk(params: UpdateBulkDealerParams): Promise<number> {
    const { data } = await this.put<number>('/dealer/update-bulk', params);

    return data;
  }

  async getSourceOptions() {
    const res = await this.get<{label: string, value: string}[]>('/dealer/source-options');

    return res.data;
  }

  async getDealerDoctypes(dealerId: number): Promise<{ doctypes: Doctype[] }> {
    const res = await this.get<{ doctypes: Doctype[] }>(`dealer/${dealerId}/doctypes`);

    return res.data;
  }

  async getDealerDocuments(dealerId: number): Promise<Document[]> {
    const res = await this.get<Document[]>(`dealer/${dealerId}/documents`);

    return res.data;
  }

  async saveDocuments(params: DealerDocumentsParams) {
    const { dealerId, doctypeId, fileIds, number, status } = params;
    const url = `dealer/${dealerId}/doctype/${doctypeId}`;
    const res = await this.post<Document>(url, { fileIds, number, status });

    return res.data;
  }

  async changeDocumentStatus(params: DealerDocumentStatusParams) {
    const { dealerId, document, status } = params;
    const url = `dealer/${dealerId}/document/${document.id}`;
    const res = await this.put<Document>(url, { status });

    return res.data;
  }

  async addCommentToDocument(params: DealerDocumentCommentParams) {
    const { dealerId, document, comment } = params;
    const url = `dealer/${dealerId}/document/${document.id}/comment`;
    const res = await this.put<Document>(url, { comment });

    return res.data;
  }

  async setOptionalDocument(params: DealerOptionalDocumentParams) {
    const { dealerId, doctypeId, isOptionalChecked } = params;
    const url = `dealer/${dealerId}/doctype/${doctypeId}/setOptional`;
    const res = await this.post<Document>(url, { isOptionalChecked });

    return res.data;
  }

  async addFarmer(dealerId: number, params: NewFarmerParams): Promise<User> {
    const res = await this.post<User>(`dealer/${dealerId}/add-farmer`, params);

    return res.data;
  }

  async exportMKTFile(dealerId: number, file: FormData): Promise<ExportMktResponse> {
    const res = await this.post<ExportMktResponse>(
      `dealer-retailer/${dealerId}/export-mkt-file`,
      file,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    return res.data;
  }

  async getSelectionDoctypes(dealerId: number): Promise<Doctype[]> {
    const res = await this.get<Doctype[]>(`dealer/${dealerId}/selection-doctypes`);

    return res.data;
  }

  async setSelectionDoctypes(params: DealerSelectionDoctypesParams) {
    const { dealerId, data } = params;
    const url = `dealer/${dealerId}/selection-doctypes`;
    const res = await this.post<Doctype[]>(url, { data });

    return res.data;
  }
}

export const dealerHttp = new DealerHttp();

interface DealerContractParams {
  userId: number;
  dealerId: number;
  fileIds: number[];
}

export interface ExportMktResponse {
  listed: number | null;
  enabled: number | null;
  unknown: number | null;
  csv: string | null;
}

export interface UpdateBulkDealerParams {
  dealerIds: number[];
  ownerId?: number;
  statusId?: number;
  priorityId?: number;
  activationId?: number;
  source?: string;
  tier?: string;
  verqorRR?: string;
  states?: string[];
  uniqueCrops?: UniqueCropProps[];
}

interface DealerDocumentsParams {
  dealerId: number;
  doctypeId: number;
  fileIds: number[];
  number?: string | null;
  status?: DocumentStatus;
}

interface DealerDocumentStatusParams {
  dealerId: number;
  document: Document;
  status: DocumentStatus;
}

interface DealerDocumentCommentParams {
  dealerId: number;
  document: Document;
  comment: string;
}

interface DealerOptionalDocumentParams {
  dealerId: number;
  doctypeId: number;
  isOptionalChecked: boolean;
}

interface DealerSelectionDoctypesParams {
  dealerId: number;
  data: Doctype[];
}

export interface NewFarmerParams {
  birthdate?: Date | null;
  companyName: string;
  email: string;
  firstName: string;
  gender: 'Mujer' | 'Hombre';
  isCompany: boolean;
  lastName: string;
  middleName: string;
  phone: string;
  zipCode: string;
  managerId: number;
}
